import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Tooltip from 'rc-tooltip';
import { toast } from 'react-toastify';
import useMerchants from 'src/dataHooks/useMerchants';
import { Table, CheckBox, Icon } from '@1c/ui';
import LinkButton from 'src/components/LinkButton';
import formatDateTime from 'src/utils/formatDateTime';
import styles from './styles.module.scss';
import PageSpin from 'src/components/PageSpin';
import Text from 'src/components/Text';
import ActionCell from './ActionCell';

const getColumns = (reloadMerchants: () => Promise<any>, hasAlternativeAccount: boolean) => [
  {
    Header: 'Merchant ID',
    accessor: 'merchantId',
    style: { textAlign: 'center' },
    width: '100%',
  },
  {
    Header: 'Название ТСП',
    accessor: 'brandName',
    style: { textAlign: 'center' },
    width: '100%',
  },
  {
    Header: 'Адрес',
    accessor: 'address',
    style: { textAlign: 'center' },
    width: '100%',
  },
  {
    Header: 'Дата регистрации ТСП',
    accessor: 'registrationDate',
    style: { textAlign: 'center' },
    Cell: ({ value }: { value: string }) => formatDateTime(value, 'DD.MM.YYYY'),
    width: '100%',
  },
  {
    Header: 'Дата получения токена',
    accessor: 'tokenEmissionTimestamp',
    style: { textAlign: 'center' },
    Cell: ({ value, original }: { value: string; original: any }) => {
      return value ? (
        <div>
          {formatDateTime(value, 'DD.MM.YYYY HH:mm')}
          {original.alternativeAccountNumber && (
            <Tooltip
              placement="bottom"
              trigger={['hover']}
              overlay={
                <span>Токен был получен для счета: {original.alternativeAccountNumber}</span>
              }
            >
              <Icon icon="info-circle" className={styles['alternative-account-info']} />
            </Tooltip>
          )}
        </div>
      ) : null;
    },
    width: '100%',
  },
  {
    Header: 'Действия',
    style: { textAlign: 'center' },
    width: '100%',
    Cell: ({ original }: { original: any }) => (
      <ActionCell
        merchant={original}
        reloadMerchants={reloadMerchants}
        hasAlternativeAccount={hasAlternativeAccount}
      />
    ),
  },
];

const getTrProps = (state: any, { original }: { original: any }) => {
  if (original.active && !original.tokenEmissionTimestamp) {
    return { className: styles['merchant-new'] };
  }
  if (!original.active) {
    return { className: styles['merchant-not-active'] };
  }
  return {};
};

const MerchantTable: React.FC = () => {
  const { organizationUeid = '', bankUeid = '' } = useParams();

  const { fetchMerchants, wasMerchantsFetched, isMerchantsLoading, merchants, merchantsError } =
    useMerchants();

  useEffect(() => {
    if (merchantsError) {
      toast.error(merchantsError.message || 'Произошла ошибка загрузки мерчантов');
    }
  }, [merchantsError]);

  const getMerchants = useCallback(
    () => fetchMerchants('getMerchants', organizationUeid, bankUeid),
    [fetchMerchants, organizationUeid, bankUeid]
  );
  const refreshMerchants = useCallback(
    () => fetchMerchants('refreshFromOpkc', organizationUeid, bankUeid),
    [fetchMerchants, organizationUeid, bankUeid]
  );

  const hasAlternativeAccounts = (merchants?.merchants || []).some(
    (merchant: any) => !!merchant?.alternativeAccountNumber
  );
  const [hasAlternativeAccount, setHasAlternativeAccount] = useState(hasAlternativeAccounts);

  useEffect(() => {
    getMerchants();
  }, [getMerchants]);

  useEffect(() => {
    setHasAlternativeAccount(hasAlternativeAccounts);
  }, [hasAlternativeAccounts]);

  const columns = useMemo(
    () => getColumns(getMerchants, hasAlternativeAccount),
    [getMerchants, hasAlternativeAccount]
  );

  const handleRefreshButtonClick = () => refreshMerchants(); // TODO: разобраться почему для просмотра списка нужны повышенные права. Если действительно надо, то добавить проверку прав

  const handleHasAlternativeAccountClick = (event: any) => {
    setHasAlternativeAccount(event.currentTarget?.checked);
  };

  if (merchantsError) {
    return (
      <div className={styles['error-refresh-container']}>
        <LinkButton onClick={handleRefreshButtonClick} disabled={isMerchantsLoading}>
          Обновить
        </LinkButton>
      </div>
    );
  }

  if (!wasMerchantsFetched) {
    return <PageSpin />;
  }

  return (
    <div className={styles['container']}>
      <div className={styles['table-title']}>
        <div>
          <CheckBox
            disabled={hasAlternativeAccounts}
            checked={hasAlternativeAccounts || hasAlternativeAccount}
            onChange={handleHasAlternativeAccountClick}
          >
            Несколько счетов в банке
          </CheckBox>
        </div>
        <div className={styles['refresh-container']}>
          {merchants?.updateTimestamp ? (
            <Text type="span" muted>
              Актуальный список ТСП на{' '}
              {formatDateTime(merchants.updateTimestamp, 'DD.MM.YYYY HH:mm')}
            </Text>
          ) : (
            ''
          )}{' '}
          <LinkButton
            className={styles['refresh-button']}
            onClick={handleRefreshButtonClick}
            disabled={isMerchantsLoading}
          >
            Обновить
          </LinkButton>
        </div>
      </div>
      <div className={styles['table']}>
        {isMerchantsLoading ? (
          <PageSpin />
        ) : (
          <Table
            columns={columns}
            data={merchants?.merchants ?? []}
            noDataText="Нет данных"
            sortable={false}
            resizable={false}
            showPagination={false}
            pageSize={merchants?.merchants?.length}
            getTrProps={getTrProps}
          />
        )}
      </div>
    </div>
  );
};

export default MerchantTable;
